import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/language-context";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { AuthService } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth";
import { getGoogleUserInfo } from "@/services/googleOAuthService";
import * as Sentry from "@sentry/react";
import { useGoogleLogin } from "@react-oauth/google";

import { Check, Eye, EyeOff, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Redirect, useLocation, useSearch } from "wouter";

declare global {
  interface Window {
    AppleID: any;
  }
}

export default function SignupPage() {
  const { t } = useLanguage();
  const { isAuthenticated, isLoading: isAuthLoading, refreshUser } = useAuth();
  const { toast } = useToast();
  const searchString = useSearch();
  const [, navigate] = useLocation();
  // Plan picked on the landing pricing card (landing.tsx handleSelectPlan); storage can throw in private mode.
  const [selectedPlan] = useState(() => {
    try {
      return sessionStorage.getItem("selectedPlan");
    } catch {
      return null;
    }
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGoogleProcessing, setIsGoogleProcessing] = useState(false);
  const [isAppleProcessing, setIsAppleProcessing] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);

  const [passwordValid, setPasswordValid] = useState({
    length: false,
    hasUppercase: false,
    hasNumber: false,
  });

  // Extract affiliate code from query string
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const affiliate = params.get("affiliate");
    if (affiliate) {
      setAffiliateCode(affiliate);
    }

    // Initialize Apple ID
    if (window.AppleID) {
      try {
        window.AppleID.auth.init({
          clientId: import.meta.env.VITE_APPLE_CLIENT_ID || "",
          scope: "name email",
          redirectURI:
            import.meta.env.VITE_APPLE_REDIRECT_URI || window.location.origin,
          usePopup: true,
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { source: "apple-id.init" },
        });
      }
    } else {
      Sentry.captureMessage(
        "window.AppleID not available — Apple Sign-In script failed to load",
        {
          level: "warning",
          tags: { source: "apple-id.init", reason: "script_missing" },
        },
      );
    }
  }, [searchString]);

  // mirror of server/src/common/password-validation.ts: /^(?=.*[A-Z])(?=.*\d).{8,}$/
  const validatePassword = (value: string) => {
    setPasswordValid({
      length: value.length >= 8,
      hasUppercase: /[A-Z]/.test(value),
      hasNumber: /[0-9]/.test(value),
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!termsAccepted) {
      toast({
        title: "Terms Required",
        description: "Please accept the Terms & Conditions to continue.",
        variant: "destructive",
      });
      return;
    }

    if (
      !passwordValid.length ||
      !passwordValid.hasUppercase ||
      !passwordValid.hasNumber
    ) {
      toast({
        title: "Invalid Password",
        description: "Please ensure your password meets all requirements.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      await AuthService.register({
        name,
        email,
        password,
        ...(affiliateCode && { affiliateCode }),
      });

      await refreshUser();

      toast({
        title: "Account Created",
        description: "Welcome to Evooluir! Redirecting to plans...",
      });

      // SPA nav: a full reload here aborted the in-flight /plans fetch (EVOOLUIR-SUBSCRIPTION-REACT-1)
      navigate("/plans");
    } catch (error) {
      // HTTP errors (ApiError) are classified + captured in api.ts.
      // Capture non-HTTP throws (parse, refreshUser fail) only.
      if (!(error instanceof ApiError)) {
        Sentry.captureException(error, {
          tags: { source: "signup.email", stage: "register" },
        });
      }
      toast({
        title: "Registration Failed",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsGoogleProcessing(true);

      try {
        const userInfo = await getGoogleUserInfo(tokenResponse.access_token);

        await AuthService.googleRegister({
          idToken: tokenResponse.access_token,
          email: userInfo.email,
          name: userInfo.name,
          ...(affiliateCode && { affiliateCode }),
        });

        await refreshUser();

        toast({
          title: "Account Created",
          description: "Welcome to Evooluir! Redirecting to plans...",
        });

        // SPA nav: a full reload here aborted the in-flight /plans fetch (EVOOLUIR-SUBSCRIPTION-REACT-1)
        navigate("/plans");
      } catch (error) {
        if (!(error instanceof ApiError)) {
          Sentry.captureException(error, {
            tags: { source: "signup.google", stage: "register" },
          });
        }
        toast({
          title: "Google Sign-Up Failed",
          description:
            error instanceof Error
              ? error.message
              : "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsGoogleProcessing(false);
      }
    },
    onError: (errorResponse) => {
      Sentry.captureMessage("Google OAuth flow error", {
        level: "warning",
        tags: { source: "signup.google", stage: "oauth_callback" },
        extra: { errorResponse },
      });
      toast({
        title: "Google Sign-Up Failed",
        description: "Failed to authenticate with Google. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAppleLogin = async () => {
    if (!window.AppleID) {
      toast({
        title: "Apple Sign-In Error",
        description:
          "Apple Sign-In is not currently available. Please try again later.",
        variant: "destructive",
      });
      return;
    }

    setIsAppleProcessing(true);

    try {
      const response = await window.AppleID.auth.signIn();

      if (response && response.authorization) {
        const { id_token, user } = response.authorization;

        // On web, user info (name, email) is only sent on the first sign-in
        let userEmail: string | undefined = undefined;
        let userName: string | undefined = undefined;

        if (user) {
          const parsedUser = typeof user === "string" ? JSON.parse(user) : user;
          userEmail = parsedUser.email;
          if (parsedUser.name) {
            userName =
              `${parsedUser.name.firstName} ${parsedUser.name.lastName}`.trim();
          }
        }

        try {
          await AuthService.appleRegister({
            identityToken: id_token,
            email: userEmail,
            name: userName,
            ...(affiliateCode && { affiliateCode }),
          });
        } catch (registerError: any) {
          // If user already exists, try to login
          if (
            registerError?.message?.includes("already") ||
            registerError?.status === 409
          ) {
            await AuthService.appleLogin({
              identityToken: id_token,
              email: userEmail,
              name: userName,
            });
          } else {
            throw registerError;
          }
        }

        await refreshUser();

        toast({
          title: "Account Created",
          description: "Welcome to Evooluir! Redirecting to plans...",
        });

        // SPA nav: a full reload here aborted the in-flight /plans fetch (EVOOLUIR-SUBSCRIPTION-REACT-1)
        navigate("/plans");
      }
    } catch (error) {
      // Don't toast if user closed the popup
      if (error && (error as any).error === "popup_closed_by_user") {
        return;
      }

      if (!(error instanceof ApiError)) {
        Sentry.captureException(error, {
          tags: { source: "signup.apple", stage: "register_or_login" },
        });
      }
      toast({
        title: "Apple Sign-Up Failed",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAppleProcessing(false);
    }
  };

  const benefits = [
    "Complete library of guided rituals & meditations",
    "Personalized daily wellness recommendations",
    "Track your progress with detailed insights",
  ];

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF68C3]" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Redirect to="/plans" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#FFF8FA] to-[#FFF0F5]">
      {/* Header */}
      <header className="p-6 lg:p-8">
        <a href="/" className="inline-block">
          <span className="font-serif text-2xl font-medium text-gray-900">
            Evooluir
          </span>
        </a>
      </header>

      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Panel - Value Proposition */}
          <div className="lg:pt-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF68C3]/10 to-[#FFA98B]/10 border border-[#FF68C3]/20 mb-6">
              <Sparkles className="w-4 h-4 text-[#FF68C3]" />
              <span className="text-sm font-medium text-gray-700">
                Start your 7-day free trial
              </span>
            </div>

            <h1 className="font-serif text-4xl lg:text-5xl font-medium text-gray-900 leading-tight mb-4">
              Begin your wellness
              <span className="block text-gradient">transformation</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-md">
              Join thousands discovering the power of daily rituals for a more
              mindful, balanced life.
            </p>

            {/* Pricing highlight */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-4xl font-bold text-gradient">$0</span>
                <span className="text-gray-600 font-medium">for 7 days</span>
              </div>
              <p className="text-sm text-gray-500">
                {selectedPlan === "monthly"
                  ? "Then $14.99/month. Cancel anytime."
                  : "Then $9.99/month billed annually. Cancel anytime."}
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-[#FF68C3] to-[#FFA98B] flex items-center justify-center mt-0.5">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <p className="text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Sign Up Form */}
          <div className="relative">
            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-8 border border-gray-100">
              <div className="text-center mb-6">
                <h2 className="font-serif text-2xl font-medium text-gray-900 mb-2">
                  Create your account
                </h2>
                {/* <p className="text-gray-500 text-sm">
                  Already have an account?{" "}
                  <a href="/api/login" className="text-[#FF68C3] font-medium hover:underline">
                    Log in
                  </a>
                </p> */}
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Jane"
                    className="h-11 rounded-xl border-gray-200 bg-white focus:border-[#FF68C3] focus:ring-[#FF68C3]/20"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="email"
                    className="text-sm font-medium text-gray-700"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="jane@example.com"
                    className="h-11 rounded-xl border-gray-200 bg-white focus:border-[#FF68C3] focus:ring-[#FF68C3]/20"
                    data-testid="input-email"
                  />
                </div>

                <div className="space-y-1.5">
  <Label
    htmlFor="password"
    className="text-sm font-medium text-gray-700"
  >
    Password
  </Label>

  <div className="relative">
    <Input
      id="password"
      type={showPassword ? "text" : "password"}
      value={password}
      onChange={handlePasswordChange}
      required
      placeholder="8+ characters"
      className="h-11 rounded-xl border-gray-200 bg-white focus:border-[#FF68C3] focus:ring-[#FF68C3]/20 pr-10"
      data-testid="input-password"
    />

    {/* Toggle Icon */}
    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
    >
      {showPassword ? (
        <EyeOff className="w-4 h-4" />
      ) : (
        <Eye className="w-4 h-4" />
      )}
    </button>
  </div>

  {password && (
    <div className="flex gap-3 text-xs mt-1.5 px-1">
      <span
        className={`flex items-center gap-1 ${
          passwordValid.length ? "text-green-600" : "text-gray-400"
        }`}
      >
        {passwordValid.length && <Check className="w-3 h-3" />}
        8+ chars
      </span>

      <span
        className={`flex items-center gap-1 ${
          passwordValid.hasUppercase ? "text-green-600" : "text-gray-400"
        }`}
      >
        {passwordValid.hasUppercase && <Check className="w-3 h-3" />}
        Uppercase
      </span>

      <span
        className={`flex items-center gap-1 ${
          passwordValid.hasNumber ? "text-green-600" : "text-gray-400"
        }`}
      >
        {passwordValid.hasNumber && <Check className="w-3 h-3" />}
        Number
      </span>
    </div>
  )}
</div>

                <div className="flex items-start gap-3 pt-1">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) =>
                      setTermsAccepted(checked as boolean)
                    }
                    className="mt-0.5 data-[state=checked]:bg-[#FF68C3] data-[state=checked]:border-[#FF68C3]"
                    data-testid="checkbox-terms"
                  />
                  <Label
                    htmlFor="terms"
                    className="text-sm text-gray-600 leading-relaxed cursor-pointer"
                  >
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#FF68C3] hover:underline" data-testid="link-signup-terms">
                      Terms
                    </a>{" "}
                    and{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#FF68C3] hover:underline" data-testid="link-signup-privacy">
                      Privacy Policy
                    </a>
                  </Label>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-12 btn-gradient text-base font-semibold rounded-xl shadow-lg shadow-[#FF68C3]/25"
                  disabled={isProcessing}
                  data-testid="button-create-account"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    "Create account"
                  )}
                </Button>

                <div className="relative py-3">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-4 text-sm text-gray-400">
                      or continue with
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="h-11 rounded-xl border-gray-200 bg-white hover:bg-gray-50 gap-2 font-medium"
                    onClick={() => handleGoogleLogin()}
                    disabled={isProcessing || isGoogleProcessing}
                    data-testid="button-google-signup"
                  >
                    {isGoogleProcessing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                    )}
                    {isGoogleProcessing
                      ? "Signing up..."
                      : "Signup with Google"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="h-11 rounded-xl bg-black text-white hover:bg-gray-900 border-black gap-2 font-medium"
                    onClick={handleAppleLogin}
                    disabled={
                      isProcessing || isGoogleProcessing || isAppleProcessing
                    }
                    data-testid="button-apple-signup"
                  >
                    {isAppleProcessing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                      </svg>
                    )}
                    {isAppleProcessing ? "Signing up..." : "Apple"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
